/**
 * Costruzione e scarico del report VEX (CycloneDX) per un thread SRP.
 *
 * Il VEX vive per-bundle nell'aggregation-service (GET /releases/:bundleProjectUuid/vex).
 * Un report può coprire più bundle: si scarica il VEX di ciascun bundle distinto presente
 * fra le CVE e si costruisce UN solo documento con UNA voce per ciascuna CVE del report:
 *  - le CVE presenti nel VEX dei bundle ma non nel report sono escluse;
 *  - gli `affects` della stessa CVE sono uniti fra tutti i bundle (dedup per `ref`);
 *  - se i bundle riportano analisi diverse per la stessa CVE vince la più conservativa
 *    (exploitable > in_triage > non analizzata > resolved > false_positive > not_affected).
 */

import { fetchVex } from '../triage/api.js';
import type { SrpCve } from './types.js';

/** Documento VEX CycloneDX opaco per il portale. */
export type VexDocument = Record<string, unknown>;

/** UUID per il serialNumber CycloneDX; fallback pseudo-univoco se crypto non disponibile. */
function generaUuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Voce `vulnerabilities[]` di un documento VEX CycloneDX (campi usati dalla fusione). */
interface VexVulnerability {
  id?: string;
  ['bom-ref']?: string;
  analysis?: { state?: string } & Record<string, unknown>;
  affects?: Array<{ ref?: string } & Record<string, unknown>>;
  [key: string]: unknown;
}

/**
 * Rango di "conservatività" dello stato di analisi CycloneDX: più basso = più cautelativo.
 * Uno stato assente (finding non ancora analizzato) sta fra in_triage e resolved.
 */
const RANGO_STATO: Record<string, number> = {
  exploitable: 0,
  in_triage: 1,
  resolved: 3,
  resolved_with_pedigree: 3,
  false_positive: 4,
  not_affected: 5,
};
const RANGO_NON_ANALIZZATO = 2;

function rangoAnalisi(v: VexVulnerability): number {
  const stato = v.analysis?.state?.toLowerCase();
  if (!stato || stato === 'not_set') return RANGO_NON_ANALIZZATO;
  return RANGO_STATO[stato] ?? RANGO_NON_ANALIZZATO;
}

/**
 * Fonde i documenti VEX CycloneDX dei bundle in un unico documento con una voce per CVE
 * (solo le CVE in `cveIds`, confronto case-insensitive, nell'ordine di `cveIds`).
 * Usa il primo documento come base per i metadati; `serialNumber` e `metadata.timestamp`
 * sono rigenerati. I `components` eventualmente presenti sono uniti (dedup per `bom-ref`).
 * Le CVE del report assenti da tutti i VEX non compaiono nel documento.
 */
export function mergeVexByCve(docs: VexDocument[], cveIds: string[]): VexDocument {
  const base = docs[0] ?? {};
  const baseMeta = (base.metadata as Record<string, unknown> | undefined) ?? {};
  const ammesse = cveIds.map((id) => id.toUpperCase());
  const ammesseSet = new Set(ammesse);

  const perCve = new Map<string, VexVulnerability[]>();
  const componentiPerRef = new Map<string, unknown>();
  for (const doc of docs) {
    const vulns = (
      Array.isArray(doc.vulnerabilities) ? doc.vulnerabilities : []
    ) as VexVulnerability[];
    for (const v of vulns) {
      const id = v.id?.toUpperCase();
      if (!id || !ammesseSet.has(id)) continue;
      const gruppo = perCve.get(id) ?? [];
      gruppo.push(v);
      perCve.set(id, gruppo);
    }
    const comps = Array.isArray(doc.components) ? doc.components : [];
    for (const c of comps) {
      const ref = (c as { ['bom-ref']?: string })['bom-ref'];
      if (ref && !componentiPerRef.has(ref)) componentiPerRef.set(ref, c);
    }
  }

  const vulnerabilities: VexVulnerability[] = [];
  for (const id of ammesse) {
    const gruppo = perCve.get(id);
    if (!gruppo || gruppo.length === 0) continue;
    const rappresentante = [...gruppo].sort((a, b) => rangoAnalisi(a) - rangoAnalisi(b))[0]!;
    const affectsPerRef = new Map<string, { ref?: string } & Record<string, unknown>>();
    for (const v of gruppo) {
      for (const a of v.affects ?? []) {
        const chiave = a.ref ?? JSON.stringify(a);
        if (!affectsPerRef.has(chiave)) affectsPerRef.set(chiave, a);
      }
    }
    const voce: VexVulnerability = { ...rappresentante };
    if (affectsPerRef.size > 0) voce.affects = Array.from(affectsPerRef.values());
    vulnerabilities.push(voce);
  }

  return {
    bomFormat: 'CycloneDX',
    specVersion: (base.specVersion as string | undefined) ?? '1.5',
    serialNumber: `urn:uuid:${generaUuid()}`,
    version: 1,
    metadata: { ...baseMeta, timestamp: new Date().toISOString() },
    ...(componentiPerRef.size > 0 ? { components: Array.from(componentiPerRef.values()) } : {}),
    vulnerabilities,
  };
}

/**
 * Scarica e assembla il VEX del report SRP: una fetch per ciascun `bundleProjectUuid`
 * distinto fra le CVE, poi fusione per CVE (`mergeVexByCve`) limitata alle CVE del report.
 * Lancia se il report ha CVE ma nessuna ha un bundle collegato; senza CVE (report creato
 * senza CVE) restituisce un VEX vuoto.
 */
export async function buildCombinedVex(cves: SrpCve[]): Promise<VexDocument> {
  if (cves.length === 0) return mergeVexByCve([], []);
  const bundleUuids = Array.from(
    new Set(cves.map((c) => c.bundleProjectUuid).filter((u): u is string => !!u)),
  );
  if (bundleUuids.length === 0) {
    throw new Error('Nessun bundle collegato alle CVE: report VEX non disponibile.');
  }
  const docs = (await Promise.all(bundleUuids.map((u) => fetchVex(u)))) as VexDocument[];
  const cveIds = Array.from(new Set(cves.map((c) => c.cveId)));
  return mergeVexByCve(docs, cveIds);
}

/** Innesca lo scarico nel browser di un oggetto come file JSON. */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
